import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import * as _ from 'lodash';
import { orderBy } from 'lodash';
import { GlobalStateService } from 'src/app/services/global-state.service';
import { PortalUrlService } from 'src/app/services/portal-url.service/portal-url.service';
import { ProjectReportService } from 'src/app/services/project-report.service';
import { IProjectReport } from 'src/app/shared/interfaces/project-report.interface';
import { PortalUrlContentDetailsModal } from 'src/app/shared/modals/portal-url-content-details.modal';
import * as JSZip from 'jszip';
import { NumberUtil, concatFilePath, getCustomerNameWithCompany, objectIsEmpty, downloadFileFromUrl, getPhotoDisplayUrl, markThumbnailFailed } from 'src/app/shared/utils/util-function';

@Component({
  selector: 'projul-project-report',
  templateUrl: './project-report.component.html',
  styleUrls: ['./project-report.component.scss']
})
export class ProjectReportComponent implements OnInit, OnChanges {

  @Input() portalUrlContentDetailsModal: PortalUrlContentDetailsModal;
  projectReport: IProjectReport;
  photoThumbUrl = getPhotoDisplayUrl;
  onThumbError = markThumbnailFailed;

  constructor(
    private projectReportService: ProjectReportService,
    private globalStateService: GlobalStateService,
    private portalUrlService: PortalUrlService
    ) {
  }

  ngOnInit(): void {
    this.globalStateService.checkOutDatedDataObservable.pipe().subscribe(async (res: any) => {
      let updatedDate = (await this.portalUrlService.getUpdatedDateOfEntity(this.portalUrlContentDetailsModal.UrlId))?.Response as any;
      if (updatedDate != null && this.projectReport && this.projectReport.UpdatedDate < updatedDate) {
        this.globalStateService.setClientPortalRelatedDataChanges();
      }
    });

    this.applyProjectReportInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-derive state on subsequent @Input updates (e.g. SignalR-driven refetch).
    // Skip the very first change — ngOnInit already calls applyProjectReportInput.
    const change = changes['portalUrlContentDetailsModal'];
    if (change && !change.firstChange) {
      this.applyProjectReportInput();
    }
  }

  private applyProjectReportInput(): void {
    this.projectReport = this.portalUrlContentDetailsModal.Item as IProjectReport;
    this.SortProjectReports()
    this.projectReport.Project.DisplayName = this.projectReportService.mapProjectCustomerName(this.projectReport.Project, true)
  }

  concatFilePath(baseUri: any, fileName: any) {
    return concatFilePath(baseUri, fileName);
  }

  isEmpty(obj: any) {
    return !obj || objectIsEmpty(obj);
  }

  /** Download filename shape: Project_Report_{date}_{client}_{project}.pdf */
  generateDownloadFilename(): string {
    const parts: string[] = [];
    
    // Get current date in yyyy-mm-dd format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    let title = this.projectReport?.Name?.trim();
    if (!title) {
      title = 'Photo_Report';
    }
    // Replace spaces with underscores
    title = title.replace(/\s+/g, '_');
    parts.push(title);
    
    // 2. Photo reports don't have document numbers, so skip that part
    
    // 3. Date (always include)
    parts.push(dateStr);

    // 4. Project Name or Client Name
    const projectName = this.projectReport?.Project?.Name || this.projectReport?.Project?.DisplayName || '';
    
    // 3. Project name (if available)
    if (projectName?.trim()) {
      parts.push(projectName.trim().replace(/\s+/g, '_'));
    } else {
      // 4. Customer name (if available)
      const customerName = getCustomerNameWithCompany(this.projectReport?.Project?.Customer);
      if (customerName) {
        parts.push(customerName.replace(/\s+/g, '_'));
      }
    }
    
    // Join parts with underscore and sanitize
    const filename = parts.join('_');
    return filename.replace(/[^a-zA-Z0-9_\-()]/g, '_') + '.pdf';
  }

  handlePrintPDF() {
    // Generate custom filename
    const customFilename = this.generateDownloadFilename();
    
    // Store original title
    const originalTitle = document.title;
    
    // Set document title to custom filename (without .pdf extension)
    document.title = customFilename.replace('.pdf', '');
    
    // Trigger print dialog
    window.print();
    
    // Restore original title after a short delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  }

  openImageInLightbox(line: any, section: any, event?: MouseEvent) {
    event?.stopPropagation();
    if (!line.UriPath || !line.FileName) return;

    const allPhotos = section.ProjectProgressReportItem
      .filter((item: any) => item.UriPath && item.FileName);

    const index = allPhotos.indexOf(line);

    this.globalStateService.setOpenLightboxForNoteObservable(allPhotos);
    this.globalStateService.setOpenLightboxObservable({
      Index: Math.max(index, 0),
      Image: line,
      isNoteList: true
    });
  }

  downloadPhoto(line: any, event?: MouseEvent) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!line?.UriPath || !line?.FileName) return;
    downloadFileFromUrl(concatFilePath(line.UriPath, line.FileName), line.FileName);
  }

  isDownloadingAll = false;
  downloadComplete = false;

  async downloadAllPhotos() {
    const allPhotos = this.projectReport.ProjectProgressReportSection
      ?.flatMap((s: any) => s.ProjectProgressReportItem)
      ?.filter((item: any) => item.UriPath && item.FileName) || [];

    if (!allPhotos.length) return;

    this.isDownloadingAll = true;
    try {
      const zip = new JSZip();
      const fetches = allPhotos.map(async (photo: any, i: number) => {
        try {
          const url = concatFilePath(photo.UriPath, photo.FileName);
          const response = await fetch(url);
          if (!response.ok) return;
          const blob = await response.blob();
          const name = photo.FileName || `photo-${i + 1}.jpg`;
          zip.file(name, blob);
        } catch { }
      });

      await Promise.all(fetches);

      const content = await zip.generateAsync({ type: 'blob' });
      const reportName = this.projectReport?.Name?.replace(/\s+/g, '_') || 'Photo_Report';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(content);
      a.download = `${reportName}_Photos.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } finally {
      this.isDownloadingAll = false;
      this.downloadComplete = true;
      setTimeout(() => this.downloadComplete = false, 3000);
    }
  }

  adjustPhotoSize(photoSize: number) {
    return (photoSize === this.projectReport.ImageSize) ? true : false
  }

  SortProjectReports() {
    if (this.projectReport && Object.entries(this.projectReport)?.length) {
      this.projectReport.ProjectProgressReportSection = orderBy(this.projectReport.ProjectProgressReportSection, (x => NumberUtil.ToNumber(x.Index)), 'asc');
      this.projectReport.ProjectProgressReportSection.forEach(report => {
        report.ProjectProgressReportItem = _.orderBy(report.ProjectProgressReportItem, (x => NumberUtil.ToNumber(x.Index)), 'asc')
      });
      this.projectReport.ProjectProgressReportSection.forEach(x => orderBy(x.ProjectProgressReportItem, (y => NumberUtil.ToNumber(y.Index)), 'asc'))
    }
  }
}
